import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  HelpCircle, BookOpen, Users, RotateCcw, History, ArrowLeftRight, Sparkles, X,
  Camera, Target, Scan, GripVertical, PlayCircle, ArrowRight, ChevronRight,
  CloudLightning, Share2, Pin, LayoutGrid,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const FIRST_VISIT_KEY = 'ds_quicktips_seen_v1';

/**
 * GameMode tips — scoring-focused.
 */
export const GAME_MODE_TIPS = [
  { icon: LayoutGrid,    color: 'bg-emerald-100 text-emerald-700',
    title: 'One screen scores it all',
    body: <>Game Mode is your scoring home — pitches, hits, outs, runners, subs and pitch
      counts all live on one screen. The <span className="font-bold text-stone-900">pitcher
      pill</span> on the mound turns <span className="font-bold text-amber-600">amber</span>,
      then <span className="font-bold text-red-600">red</span>, as your pitcher approaches the
      league pitch limit — and use the <span className="font-bold text-stone-900">− / +</span>{' '}
      buttons beside it to adjust the count any time.</> },
  { icon: BookOpen,      color: 'bg-violet-100 text-violet-700',
    title: 'Where is a button?',
    body: <>Open the <span className="font-bold text-stone-900">Key</span> tab on the
      sidebar, tap any abbreviation, and the app will jump you straight to the
      matching button — pulsing in amber until you press it.</> },
  { icon: Users,         color: 'bg-emerald-100 text-emerald-700',
    title: 'Wrong batter up?',
    body: <>Tap the correct player in your team&apos;s lineup row at the bottom of this
      page. The app instantly switches who&apos;s at bat without ending the current at-bat.</> },
  { icon: RotateCcw,     color: 'bg-amber-100 text-amber-700',
    title: 'Made a mistake?',
    body: <>The <span className="font-bold text-stone-900">Undo</span> button rewinds
      one play per tap. Use it for fat-fingered ball/strike mistakes or to back out
      a hit you logged on the wrong batter.</> },
  { icon: History,       color: 'bg-blue-100 text-blue-700',
    title: 'Need to undo many plays at once?',
    body: <>Look at the <span className="font-bold text-stone-900">Play-by-Play</span>{' '}
      log. <span className="font-bold">Tap any row</span> and the app will rewind back
      to that exact play — useful when you realize a mistake happened 5 plays ago.</> },
  { icon: ArrowLeftRight, color: 'bg-orange-100 text-orange-700',
    title: 'Need to sub a player mid-at-bat?',
    body: <>Tap the orange <span className="font-bold text-stone-900">Quick Sub</span>{' '}
      button. Swap a batter, a runner, or a fielder without ending the at-bat or
      losing the count.</> },
  { icon: CloudLightning, color: 'bg-yellow-100 text-yellow-800',
    title: 'Lightning or rain? Broadcast a delay',
    body: <>Tap <span className="font-bold text-stone-900">Lightning Delay</span> at the top
      of Game Mode and pick a wait time. Parents instantly see a delay banner on the live page
      so nobody&apos;s sitting in their car wondering what&apos;s going on.</> },
  { icon: Share2,        color: 'bg-emerald-100 text-emerald-700',
    title: 'Share the live score with family',
    body: <>From the Parent View, tap <span className="font-bold text-stone-900">Share Live
      Score with Family</span> — grandparents, aunts, and out-of-town parents get a real-time
      page that updates every play. No app install required.</> },
  { icon: Pin,           color: 'bg-amber-100 text-amber-700',
    title: 'Pin an important coach message',
    body: <>On the <span className="font-bold text-stone-900">Messages</span> page, hit the
      📌 next to your own message — it becomes a sticky banner at the top of Parent View
      for 7 days. The app will even auto-suggest it when you type words like &ldquo;rain&rdquo;
      or &ldquo;moved to&rdquo;.</> },
  { icon: Sparkles,      color: 'bg-amber-100 text-amber-800',
    title: 'Stuck on the batting order?',
    body: <>Head to the <span className="font-bold text-stone-900">Lineup</span> page and tap
      the amber <span className="font-bold">✨ AI Lineup</span> button. Diamond Scorer
      reads every at-bat your team has logged and builds a 9-spot order in seconds.</> },
  { icon: HelpCircle,    color: 'bg-stone-200 text-stone-700',
    title: 'Pitcher rest is automatic',
    body: <>Trying to put in a pitcher who threw too many pitches yesterday? The app
      warns you before you lock it in. Set rest rules on the Pitchers &amp; Counts page
      to match your league.</> },
];

/**
 * GameMode tour — 3 most important buttons. Will be passed to <QuickTipsButton tour={...}>.
 * Each step references a real data-testid and shows a banner explaining what it does.
 */
export const GAME_MODE_TOUR = [
  { testId: 'gm-broadcast-diamond',
    title: 'Step 1 of 4: THE DIAMOND',
    body: 'Runners show up here in real time. Tap an EMPTY base to place a runner (great when you start scoring late) or tap an OCCUPIED base to advance, score, or correct that runner.' },
  { testId: 'gm-hits-btn',
    title: 'Step 2 of 4: HITS / REACH BASE',
    body: 'Tap this GREEN button whenever the ball goes into play. The dialog that opens lets you choose between Single, Double, Triple, HR, HBP, Sac, FC, and Error.' },
  { testId: 'gm-quick-sub-btn',
    title: 'Step 3 of 4: QUICK SUB',
    body: 'Need to swap a batter, a runner, or a fielder mid-at-bat? Tap this ORANGE button. The count and inning stay intact — only the player changes.' },
  { testId: 'gm-play-log',
    title: 'Step 4 of 4: PLAY-BY-PLAY LOG',
    body: 'Made a mistake 5 plays ago? Just TAP any row in this log and the app will rewind everything back to that point. Way faster than tapping Undo over and over.' },
];

/**
 * Field page tips — positioning-focused.
 */
export const FIELD_TIPS = [
  { icon: LayoutGrid,  color: 'bg-emerald-100 text-emerald-700',
    title: 'Field + Scoring auto-flip at every third out',
    body: <>When your team takes the field, the app shows this page. When they come up
      to bat, it switches you to <span className="font-bold text-stone-900">Scoring</span>.
      Want every button on one screen instead? Switch to <span className="font-bold text-stone-900">Game
      Mode</span> — all three views stay perfectly in sync, so any play you log
      anywhere updates everywhere.</> },
  { icon: BookOpen,    color: 'bg-violet-100 text-violet-700',
    title: 'Find any position fast',
    body: <>Tap a position abbreviation (P, C, SS, LF, CF, RF) on the
      <span className="font-bold text-stone-900"> Key</span> page and the matching
      dot on the diamond will glow amber.</> },
  { icon: Target,      color: 'bg-emerald-100 text-emerald-700',
    title: 'Tap a position dot to assign a player',
    body: <>Tap any position on the diamond and pick a player from the list. The
      number on the dot updates instantly. Save when you&apos;re done.</> },
  { icon: PlayCircle,  color: 'bg-red-100 text-red-700',
    title: 'Pitcher pitch counts are automatic',
    body: <>The pitch counter on the right tracks every ball, strike, foul, and hit.
      You&apos;ll see a yellow warning before the limit and a red one when reached.</> },
  { icon: CloudLightning, color: 'bg-yellow-100 text-yellow-800',
    title: 'Lightning delay? One tap notifies parents',
    body: <>Tap the <span className="font-bold text-stone-900">Lightning Delay</span> button
      and choose a wait time. The Parent View instantly shows a delay banner so families
      know to stay (or grab a coffee).</> },
  { icon: Camera,      color: 'bg-blue-100 text-blue-700',
    title: 'Got a handwritten field card?',
    body: <>Use the <span className="font-bold">Scan Card</span> button on the Lineup
      page to snap a photo and let AI digitize the players for you.</> },
  { icon: Sparkles,    color: 'bg-amber-100 text-amber-800',
    title: 'AI Lineup builds your batting order',
    body: <>On the Lineup page, the amber <span className="font-bold">✨ AI Lineup</span>{' '}
      button reads your stats and writes you a 9-spot order — with reasoning for every pick.</> },
];

/**
 * Lineup page tips — lineup management focused.
 */
export const LINEUP_TIPS = [
  { icon: Sparkles,      color: 'bg-amber-100 text-amber-800',
    title: 'AI Lineup builds the order for you',
    body: <>Tap the amber <span className="font-bold text-stone-900">✨ AI Lineup</span>{' '}
      button to have Diamond Scorer read every at-bat your team has logged and write
      a 9-spot batting order — with reasoning for every pick.</> },
  { icon: Scan,          color: 'bg-green-100 text-green-700',
    title: 'Scan a handwritten lineup card',
    body: <>The <span className="font-bold text-stone-900">Scan Card</span> button takes
      a photo of any paper lineup and digitizes the entire batting order in seconds — no
      typing.</> },
  { icon: GripVertical,  color: 'bg-blue-100 text-blue-700',
    title: 'Drag to reorder',
    body: <>Long-press any player row and drag them up or down to change the batting
      order. Or tap two rows in sequence to swap them.</> },
  { icon: Users,         color: 'bg-violet-100 text-violet-700',
    title: 'DP / FLEX (softball)',
    body: <>If you&apos;re playing softball with a Designated Player, enable DP/FLEX
      in your team settings. The app will validate your lineup so you don&apos;t accidentally
      break the rule.</> },
  { icon: ArrowRight,    color: 'bg-emerald-100 text-emerald-700',
    title: 'Save before scoring',
    body: <>After editing the lineup, tap <span className="font-bold text-stone-900">Save
      Changes</span> before heading to Game Mode. Otherwise the order won&apos;t
      stick mid-game.</> },
];

/**
 * Reusable Quick Tips floating button.
 *
 * Props:
 *   tips      — array of { icon, color, title, body } (REQUIRED)
 *   tour      — optional array of { testId, title, body } steps. Renders a "Take a tour"
 *               CTA inside the dialog and animates the user through the targets.
 *   storageKey— localStorage key for the first-visit pulse flag (so each page can pulse once).
 */
export default function QuickTipsButton({ tips, tour = null, storageKey = FIRST_VISIT_KEY }) {
  const [open, setOpen] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [tourStep, setTourStep] = useState(-1); // -1 = inactive
  const tourElRef = useRef(null);

  // First-visit pulse
  useEffect(() => {
    const seen = (() => {
      try { return localStorage.getItem(storageKey); } catch { return null; }
    })();
    if (!seen) {
      setShouldPulse(true);
      const t = setTimeout(() => setShouldPulse(false), 12000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [storageKey]);

  const markSeen = () => {
    try { localStorage.setItem(storageKey, '1'); } catch {}
  };

  const handleOpen = () => {
    setOpen(true);
    setShouldPulse(false);
    markSeen();
  };

  // ---- TOUR LOGIC ----
  // When tourStep advances, find the target element, add the highlight class,
  // scroll it into view. On unmount or step change, remove the highlight.
  useEffect(() => {
    if (tourStep < 0 || !tour || tourStep >= tour.length) return undefined;
    const stepDef = tour[tourStep];
    const el = document.querySelector(`[data-testid="${stepDef.testId}"]`);
    if (!el) return undefined;
    el.classList.add('ds-highlight-target');
    tourElRef.current = el;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
    return () => {
      if (tourElRef.current) {
        tourElRef.current.classList.remove('ds-highlight-target');
        tourElRef.current = null;
      }
    };
  }, [tourStep, tour]);

  const startTour = () => {
    setOpen(false);
    // Wait for dialog close animation, then highlight first step
    setTimeout(() => setTourStep(0), 300);
  };

  const nextTour = () => {
    if (!tour) return;
    if (tourStep + 1 >= tour.length) {
      // Finish
      setTourStep(-1);
    } else {
      setTourStep(tourStep + 1);
    }
  };

  const skipTour = () => setTourStep(-1);

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          'fixed top-3 right-3 z-40',
          'flex items-center gap-1.5 px-3 py-2 rounded-full',
          'bg-amber-500 hover:bg-amber-400 active:bg-amber-600',
          'text-stone-900 font-bold text-xs uppercase tracking-wider',
          'shadow-lg border-2 border-amber-300',
          'transition-transform hover:scale-105 active:scale-95',
          shouldPulse && 'ds-quicktips-pulse'
        )}
        data-testid="quick-tips-btn"
        aria-label="Open Quick Tips"
      >
        <HelpCircle size={14} className="shrink-0" />
        Quick Tips
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-stone-50 border-stone-200 max-w-md max-h-[85vh] overflow-y-auto"
          data-testid="quick-tips-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2 text-stone-900"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <HelpCircle size={20} className="text-amber-600" />
              QUICK TIPS
            </DialogTitle>
            <p className="text-xs text-stone-500 -mt-1">
              {tips.length} things even seasoned coaches sometimes miss.
            </p>
          </DialogHeader>

          {/* Take-a-tour CTA — only shown when this page provides tour steps */}
          {tour && tour.length > 0 && (
            <button
              type="button"
              onClick={startTour}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-stone-900 font-bold rounded-xl p-3 mt-2 flex items-center gap-3 border-2 border-amber-600 shadow-sm transition-transform hover:scale-[1.01] active:scale-[0.99]"
              data-testid="start-tour-btn"
            >
              <PlayCircle size={22} className="shrink-0" />
              <div className="text-left flex-1">
                <p className="text-sm font-extrabold uppercase tracking-wider">Take a 30-second tour</p>
                <p className="text-[11px] text-stone-700 normal-case font-normal">
                  We&apos;ll point at {tour.length} key buttons in order. Skip anytime.
                </p>
              </div>
              <ChevronRight size={20} className="shrink-0" />
            </button>
          )}

          <div className="space-y-3 mt-3">
            {tips.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-xl p-3 border border-stone-200 shadow-sm flex gap-3"
                  data-testid={`quick-tip-${i}`}
                >
                  <div className={cn('shrink-0 w-9 h-9 rounded-full flex items-center justify-center', tip.color)}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-stone-900 mb-0.5">
                      {i + 1}. {tip.title}
                    </p>
                    <p className="text-[13px] text-stone-600 leading-relaxed">
                      {tip.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            onClick={() => setOpen(false)}
            className="w-full bg-green-800 hover:bg-green-700 text-white mt-3"
            data-testid="quick-tips-got-it"
          >
            <X size={14} className="mr-1" /> Got it
          </Button>
        </DialogContent>
      </Dialog>

      {/* TOUR OVERLAY — fixed banner at the top with current step + Next/Skip */}
      {tourStep >= 0 && tour && tour[tourStep] && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] flex items-start justify-center p-3 pointer-events-none"
          data-testid="tour-overlay"
        >
          <div className="bg-stone-900/95 backdrop-blur-md border-2 border-amber-500 rounded-2xl shadow-2xl p-4 max-w-md w-full pointer-events-auto" data-testid="tour-banner">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-500 text-stone-900 flex items-center justify-center font-bold text-lg">
                {tourStep + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-300 text-[10px] uppercase tracking-wider font-bold mb-1">
                  Tour · {tourStep + 1} of {tour.length}
                </p>
                <p className="text-white font-bold text-sm leading-tight mb-1.5">
                  {tour[tourStep].title}
                </p>
                <p className="text-stone-300 text-xs leading-relaxed">
                  {tour[tourStep].body}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                onClick={skipTour}
                variant="outline"
                size="sm"
                className="border-stone-600 text-stone-300 hover:bg-stone-800 flex-1"
                data-testid="tour-skip-btn"
              >
                Skip tour
              </Button>
              <Button
                onClick={nextTour}
                size="sm"
                className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold flex-1"
                data-testid="tour-next-btn"
              >
                {tourStep + 1 >= tour.length ? 'Finish' : 'Next'}
                <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
